import { PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Drawer,
  Form,
  Input,
  message,
  Modal,
  Select,
  Table,
  Typography
} from "antd";
import React, { useContext, useEffect, useState } from "react";
import { SessionContext } from "../../../App";
import {
  AdminUpdateCompanyUserRequest,
  ChangePasswordRequest,
  GetAllCompanyUserRequest,
  SignUpRequest
} from "../../../constants/apiRequestResponse";
import { Company, CompanyUser, Gender } from "../../../constants/types";
import {
  adminChangeCompanyUserPassword,
  adminUpdateCompanyUser,
  deleteUser,
  getAllCompanies,
  getAllCompanyUsers,
  signup,
} from "../../../services/api";
import { parseTime } from "../../../util";

export default function CompanyUserTab() {
  enum DrawerModes {
    closed,
    add,
    edit,
  }
  const [form] = Form.useForm();
  const [resetPasswordForm] = Form.useForm();
  const context = useContext(SessionContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddLoading, setIsAddLoading] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [companyUsers, setCompanyUsers] = useState<CompanyUser[] | undefined>();
  const [companies, setCompanies] = useState<Company[] | undefined>();
  const [drawerMode, setDrawerMode] = useState<DrawerModes>(DrawerModes.closed);

  const isDrawerOpen = drawerMode !== DrawerModes.closed;

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
    },
    {
      title: "Company",
      dataIndex: "companyName",
      render: (companyName: string, companyUser: CompanyUser) => {
        return (
          <a href={`/company/?companyId=${companyUser.companyId}`}>
            {companyName}
          </a>
        );
      },
      sorter: (a, b) => {
        return a.companyName.localeCompare(b.companyName);
      },
    },
    {
      title: "Role",
      dataIndex: "role",
    },
    {
      title: "Email",
      dataIndex: "email",
    },
    {
      title: "Mobile",
      dataIndex: "mobile",
    },
    {
      title: "Gender",
      dataIndex: "gender",
    },
    {
      title: "Created at",
      dataIndex: "createdAt",
      render: (time) => parseTime(time),
    },
    {
      title: "Last login at",
      dataIndex: "lastLoginAt",
      render: (time) => parseTime(time),
    },
    {
      title: "Action",
      fixed: "right" as "right",
      width: 150,
      render: (companyUser: CompanyUser) => {
        return (
          <div style={{display: 'flex'}}>
            <Button
              type="link"
              data-testclass={`edit-company-user-btn`}
              onClick={() => {
                setDrawerMode(DrawerModes.edit);

                form.setFieldsValue(companyUser);
              }}
            >
              Edit
            </Button>
            {
              context?.companyUser?._id !== companyUser._id ? (
                <Button
                type="link"
                danger
                onClick={() => {
                  Modal.confirm({
                    title: 'Confirm delete user?',
                    onOk(){
                        return deleteUser(companyUser._id)
                        .then(() => {
                          message.success(`Deleted user`)
                          getData();
                        })
                        .catch(err => {
                          message.error(err.toString())
                        })
                    }
                  })
                }}
              >
                Delete
              </Button>
              )
              :null
            }
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    getData();
  }, []);

  const getData = () => {
    setIsLoading(true);
    const req1: GetAllCompanyUserRequest = {};
    getAllCompanyUsers(req1)
      .then((res) => {
        if (!res.success) throw new Error(res.message);
        setCompanyUsers(res.companyUsers);
      })
      .catch((err) => {
        message.error(err.toString());
      })
      .finally(() => {
        setIsLoading(false);
      });

    getAllCompanies()
      .then((res) => {
        if (!res.success) throw new Error(res.message)
        setCompanies(res.companies)
      })
      .catch((err) => {
        message.error(err.toString())
      })
  };

  return (
    <div
      data-testid="company-user-tab" 
      style={{
        width: "100%",
      }}
    >
      <div

        style={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 10,
        }}
      >
        <Typography.Title level={4}>User</Typography.Title>
        <Button
          type="primary"
          onClick={() => {
            setDrawerMode(DrawerModes.add);
          }}
          data-testid="create-company-user-btn"
          icon={<PlusOutlined />}
        >
          Create
        </Button>
      </div>

      <Table
        loading={isLoading}
        rowKey={"_id"}
        columns={columns}
        scroll={{
          x: columns.length * 150
        }}
        dataSource={companyUsers?.map((cu) => {
          const company = companies?.find((c) => c._id === cu.companyId);
          return {
            ...cu,
            companyName: company?.name,
          };
        })}
        expandable={{
          expandedRowRender: (companyUser: CompanyUser) => {
            return (
              <div
                style={{
                  display: "flex",
                  width: window.innerWidth * 0.9,
                  gap: 10,
                }}
              >
                <Card style={{ flex: 2 }}>
                  <Typography.Title level={5}>Reset password</Typography.Title>

                  <Form
                    form={resetPasswordForm}
                    layout="vertical"
                    onFinish={async (v: {
                      oldPassword: string;
                      newPassword: string;
                      confirmNewPassword: string;
                    }) => {
                      try {
                        setIsLoading(true);
                        if (v.newPassword !== v.confirmNewPassword)
                          throw new Error(`New passwords don't match.`);

                        const req: ChangePasswordRequest = {
                          companyUserId: companyUser?._id,
                          oldPassword: v.oldPassword,
                          newPassword: v.newPassword,
                        };

                        await adminChangeCompanyUserPassword(req);

                        message.success(`Password reset successful`);
                        resetPasswordForm.resetFields();

                        setIsLoading(false);
                      } catch (err) {
                        message.error(
                          typeof err === "string"
                            ? err
                            : typeof err === "object"
                            ? err?.toString()
                            : `Something went wrong`
                        );
                        setIsLoading(false);
                      }
                    }}
                  >
                    <Form.Item
                      label="Old password"
                      name="oldPassword"
                      rules={[{ required: true }]}
                    >
                      <Input.Password />
                    </Form.Item>
                    <Form.Item
                      label="New password"
                      name="newPassword"
                      rules={[{ required: true }]}
                    >
                      <Input.Password />
                    </Form.Item>
                    <Form.Item
                      label="Confirm new password"
                      name="confirmNewPassword"
                      rules={[{ required: true }]}
                    >
                      <Input.Password />
                    </Form.Item>
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 5,
                      }}
                    >
                      <Button
                        htmlType="submit"
                        type="primary"
                        loading={isLoading}
                      >
                        Reset
                      </Button>
                    </div>
                  </Form>
                </Card>
              </div>
            );
          },
        }}
      />

      <Drawer
        title={drawerMode === DrawerModes.add ? "Add a new user" : `Edit user`}
        placement="right"
        visible={isDrawerOpen}
        onClose={() => {
          setDrawerMode(DrawerModes.closed);
          form.resetFields();
        }}
        destroyOnClose={true}
      >
        <Form
          form={form}
          layout="vertical"
          onFinishFailed={(v) => {}}
          onFinish={async (v) => {
            if (drawerMode === DrawerModes.edit) {
              setIsEditLoading(true);
              const req1: AdminUpdateCompanyUserRequest = {
                companyUser: v,
              };
              adminUpdateCompanyUser(req1)
                .then((res2) => {
                  message.success(`Updated successfully`);

                  setDrawerMode(DrawerModes.closed);
                  getData();
                })
                .catch((err) => {
                  message.error(err.toString());
                })
                .finally(() => {
                  setIsEditLoading(false);
                });
              return;
            }
            if (v.password !== v.passwordConfirm) {
              message.error(`Password and Confirm passwords do not match`);
              return;
            }

            setIsAddLoading(true);

            const req1: SignUpRequest = {
              companyUser: {
                name: v.name,
                companyId: v.companyId,
                gender: v.gender,
                email: v.email,
                mobile: v.mobile,
                password: v.password,
                role: v.role,
              },
            };
            signup(req1)
              .then(() => {
                message.success(`Created successfully`);

                setDrawerMode(DrawerModes.closed);
                getData();
              })
              .catch((err) => {
                message.error(err.toString());
              })
              .finally(() => {
                setIsAddLoading(false);
              });
          }}
        >
          <Form.Item label="ID" name="_id" hidden>
            <Input disabled />
          </Form.Item>
          <Form.Item
            label="Company"
            name="companyId"
            rules={[{ required: true, message: "Company is required" }]}
          >
            <Select>
              {companies?.map((company) => {
                return (
                  <Select.Option key={company.name} value={company._id}>
                    {company.name}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: "Name is required" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Email"
            name="email"
            rules={[{ required: true, message: "Email is required" }]}
          >
            <Input />
          </Form.Item>
          {drawerMode === DrawerModes.add ? (
            <Form.Item
              label="Password"
              name="password"
              rules={[{ required: true, message: "Password is required" }]}
            >
              <Input.Password />
            </Form.Item>
          ) : null}
          {drawerMode === DrawerModes.add ? (
            <Form.Item
              label="Confirm password"
              name="passwordConfirm"
              rules={[
                { required: true, message: "Confirm password is required" },
              ]}
            >
              <Input.Password />
            </Form.Item>
          ) : null}
          <Form.Item label="Mobile" name="mobile">
            <Input />
          </Form.Item>
          <Form.Item
            label="Gender"
            name="gender"
            rules={[
              {
                required: false,
                type: "enum",
                enum: [Gender.Male, Gender.Female, Gender.Other],
              },
            ]}
          >
            <Select showSearch placeholder={Gender.Male}>
              {[Gender.Male, Gender.Female, Gender.Other].map((gender) => {
                return (
                  <Select.Option key={gender} value={gender}>
                    {gender}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={isEditLoading || isAddLoading}
              >
                {drawerMode === DrawerModes.edit ? "Save" : "Add"}
              </Button>
            </Form.Item>
          </div>
        </Form>
      </Drawer>
    </div>
  );
}
